import React from 'react';
import { InfoCircleOutlined } from '@ant-design/icons';
import { Tag, Tooltip, Typography } from 'antd';
import type { FormInstance } from 'antd';
import type { NamePath } from 'antd/es/form/interface';

interface InheritableProps<T> {
  label: string;
  tooltip?: React.ReactNode;
  name: NamePath;
  value?: T | null;
  inheritedValue: T;
  form: FormInstance;
  disabled?: boolean;
  children: (slot: {
    value: T;
    onChange: (next: T | null) => void;
    isInherited: boolean;
    disabled?: boolean;
  }) => React.ReactNode;
}

/**
 * Universal inheritance wrapper. Renders label + "Inherited" tag + Reset link;
 * passes resolved value + onChange to a child render function (slot). The slot
 * brings any antd control — Switch, InputNumber, Select, DatePicker, custom —
 * and wires it up however the control expects.
 *
 *   null → "Inherited" (slot receives inheritedValue)
 *   T    → explicit override; "Reset" link clears back to null
 */
const Inheritable = <T,>({
  label,
  tooltip,
  name,
  value,
  inheritedValue,
  form,
  disabled,
  children,
}: InheritableProps<T>) => {
  const isInherited = value == null;
  const slotValue = (value ?? inheritedValue) as T;
  const onChange = (next: T | null) => form.setFieldValue(name, next);

  return (
    <div className="flex items-center justify-between min-h-[40px] w-full">
      <span className="text-[14px] font-medium text-[#5C656E]">
        {label}
        {tooltip && (
          <Tooltip title={tooltip}>
            <InfoCircleOutlined className="!ml-1 cursor-pointer" />
          </Tooltip>
        )}
        {isInherited && (
          <Tag color="default" className="!ml-2 !text-[11px]">
            Inherited
          </Tag>
        )}
      </span>
      <div className="flex items-center gap-2 shrink-0">
        {!isInherited && !disabled && (
          <Typography.Link
            className="!text-[12px] whitespace-nowrap"
            onClick={() => onChange(null)}
          >
            Reset
          </Typography.Link>
        )}
        {children({ value: slotValue, onChange, isInherited, disabled })}
      </div>
    </div>
  );
};

export default Inheritable;
